import { ServerStorage, Storage } from "../util/Storage";
import AdBlockerModal from "./AdBlockerModal";

const VISIT_MULTIPLIER = 5; // user will be shown the blocker every 5 visits after the 1st
const MESSAGE_DURATION = 5;
const AD_BLOCKER_NAME_KEY = "bbAdBlocker";
const AD_BLOCKER_INCREMENTED_KEY = "bbAbIncremented";
const AD_BLOCKER_MULTIPLIER_KEY = "bbAbVisitMultiplier";
const AD_BLOCKER_DURATION_KEY = "bbAbMessageDuration";
const AD_BLOCKER_VISITS_KEY = "bbAbVisits";
const AD_BLOCKER_MESSAGE_KEY = "bbAbMessage";
const AD_BLOCKER_COOKIE_EXPIRATION = 365; // days
const CONTAINER_ID = "adBlockerContainer";
const MESSAGE_ID = "adBlockerMessage";
const CONTINUE_LINK_ID = "adBlockerContinue";
const TIMER_ID = "adBlockerTimer";
const DEFAULT_LOCAL_STORAGE_OBJECT = {
    [AD_BLOCKER_DURATION_KEY]: MESSAGE_DURATION,
    [AD_BLOCKER_MULTIPLIER_KEY]: VISIT_MULTIPLIER,
    [AD_BLOCKER_MESSAGE_KEY]: AdBlockerModal.getBlockerMessage()
};

export type StorageArg = { session: ServerStorage, local: ServerStorage, cookie: ServerStorage };

/**
 * @class AdBlocker
 */
export default class AdBlocker {

    /**
     * Serves modal to users who utilize ad blockers
     * asking them to add our site(s) to allowlist.
     */

    static blockTheAdBlocker() {

        if (!validBody()) {
            return;
        }

        insertFakeAdInDom();

        checkForAdBlockerScript()
            .then((result) => {
                checkForAdBlockerBehavior({
                    hasAdBlocker: handleAdBlocker,
                    withoutAdBlocker: clearAdBlockerData,
                    javascriptTest: result
                });
            });
    }

}

/**
 * Confirms document has valid body
 * for testing in consuming apps.
 *

 * @returns {boolean}
 */
function validBody() {
    return Boolean(document.body);
}

/**
 * Creates fake ad div and places
 * it on the page with styles.
 */
function insertFakeAdInDom() {
    const fakeAd = document.createElement("div");

    fakeAd.setAttribute("class", AdBlockerModal.fakeAdClasses());
    fakeAd.setAttribute("style", AdBlockerModal.fakeAdStyles());
    document.body.appendChild(fakeAd);
}

/**
 * Fires GA event only if adBlocker
 * usage has been detected
 *
 * @param {String} source
 */
function fireGAEvent(source: string) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
        event: "adBlockerInUse",
        eventCategory: "Ad Blocker",
        eventAction: "detected ad blocker use",
        eventLabel: source
    });
}

/**
 * Uses html div and computed style attributes to
 * test for ad blocker. If user does, initiates the blocking
 * workflow. If user does not have blocker,
 * clears data from local/session/cookie
 * storage and exits.
 *
 * @param {object} options
 * @param {Function} options.hasAdBlocker
 * @param {Function} options.withoutAdBlocker
 * @param {Boolean} options.javascriptResult
 */
function checkForAdBlockerBehavior(
    { hasAdBlocker, withoutAdBlocker, javascriptTest }:
    { hasAdBlocker: () => void, withoutAdBlocker: () => void, javascriptTest: boolean }
) {

    if (javascriptTest) {
        fireGAEvent("javascript");
    }

    if (window.getComputedStyle !== undefined) {

        setTimeout(() => {
            const fakeAdElement = window.document.getElementsByClassName("pub_300x250")[0];
            const fakeAdTemp = window.getComputedStyle(fakeAdElement, null);

            if (fakeAdTemp && (fakeAdTemp.getPropertyValue("display") === "none" || fakeAdTemp.getPropertyValue("visibility") === "hidden" || fakeAdTemp.getPropertyValue("height") === "0")) {
                fireGAEvent("fake element");
                hasAdBlocker();

                return;
            }

            withoutAdBlocker();
        }, 2500);
    }

}

/**
 * Clears data from sessionStorage, localStorage
 * and cookies
 */
export function clearAdBlockerData() {
    const _storage = {
        local: Storage.createLocalStorage(),
        session: Storage.createSessionStorage(),
        cookie: Storage.createCookieStorage(AD_BLOCKER_COOKIE_EXPIRATION)
    };

    if (!userHasStorage(_storage)) {
        return;
    }

    const sessionStorage = _storage.session;
    const localStorage = _storage.local;
    const cookieStorage = _storage.cookie;
    const adBlockerObject = DEFAULT_LOCAL_STORAGE_OBJECT;

    // @ts-expect-error this doesn't seem correct but not changing logic in Q4
    if (sessionStorage && !sessionStorage.AD_BLOCKER_NAME_KEY) {
        cookieStorage.remove(AD_BLOCKER_VISITS_KEY);
    }

    Object.keys(adBlockerObject).forEach((key) => localStorage.remove(key));

    sessionStorage.set(AD_BLOCKER_INCREMENTED_KEY, "true");
}

/**
 * User has an ad blocker, now deduce whether
 * they should have the blocker blocked, and
 * compile data needed to create and render
 * the modal.
 */
export function handleAdBlocker() {
    const _storage = {
        local: Storage.createLocalStorage(),
        session: Storage.createSessionStorage(),
        cookie: Storage.createCookieStorage(AD_BLOCKER_COOKIE_EXPIRATION)
    };

    if (!userHasStorage(_storage) || userShouldNotBeBlocked(_storage)) {
        return;
    }

    _storage.session.set(AD_BLOCKER_NAME_KEY, "true");
    setAdBlockerData(_storage);
    blockTheAdBlocker(_storage);
}

/**
 * Sets relevant data into user's
 * localStorage, sessionStorage and cookies
 */
export function setAdBlockerData(storage: StorageArg) {
    const sessionStorage = storage.session;
    const localStorage = storage.local;
    const adBlockerObject = DEFAULT_LOCAL_STORAGE_OBJECT;
    const cookieStorage = storage.cookie;

    const visitsFromCookie = cookieStorage.get(AD_BLOCKER_VISITS_KEY);
    const visits = visitsFromCookie ? parseInt(visitsFromCookie, 10) + 1 : 1;

    // @ts-expect-error this doesn't seem correct but not changing logic in Q4
    cookieStorage.set(AD_BLOCKER_VISITS_KEY, visits, AD_BLOCKER_COOKIE_EXPIRATION);

    Object.keys(adBlockerObject)
        .forEach((key) =>
            localStorage.set(key, adBlockerObject[key as keyof typeof adBlockerObject] as string));

    sessionStorage.set(AD_BLOCKER_INCREMENTED_KEY, "true");
}

/**
 * Determines if Ad Blocker message
 * should be displayed based on presence
 * of ad blocker and user's total number
 * of visits
 */
export function blockTheAdBlocker(storage: StorageArg) {
    const visits = storage.cookie.get(AD_BLOCKER_VISITS_KEY);
    const multiplier = getMultiplier(storage);

    // @ts-expect-error this doesn't seem correct but not changing logic in Q4
    if (!blockerShouldDisplay(visits, multiplier)) {
        return;
    }

    renderMessage(storage);
}

/**
 * Creates modal
 * Adds listener on false-positive link
 * to trigger Google Analytics event when
 * link is clicked
 */
export function renderMessage(storage: StorageArg) {
    const message = getMessage(storage);
    const duration = getDuration(storage);

    if (!(document && message && duration)) {
        return;
    }

    const container = AdBlockerModal.renderModal(message, duration);

    document.body.appendChild(container);
    // @ts-expect-error this doesn't seem correct but not changing logic in Q4
    document.getElementById(MESSAGE_ID).focus();

    // @ts-expect-error this doesn't seem correct but not changing logic in Q4
    window.onLoad = countdownAndReset(duration);
}

/**
 * Retrieves message for modal from
 * localStorage or uses default message
 * from this file
 */
export function getMessage(storage: StorageArg) {
    if (!userHasStorage(storage)) {
        return AdBlockerModal.getBlockerMessage();
    }

    return storage.session.get(AD_BLOCKER_MESSAGE_KEY) || AdBlockerModal.getBlockerMessage();
}

/**
 * Retrieves duration of countdown for modal
 * from localStorage or uses default duration
 * from this file
 */
function getDuration(storage: StorageArg) {
    if (!userHasStorage(storage)) {
        return MESSAGE_DURATION;
    }

    return storage.local.get(AD_BLOCKER_DURATION_KEY) || MESSAGE_DURATION;
}

/**
 * Retrieves muliplier used to determine
 * is user should be shown modal on this
 * visit (every 5th visit, for example)
 * from localStorage or uses default
 * multiplier from this file
 */
function getMultiplier(storage: StorageArg) {
    if (!userHasStorage(storage)) {
        return VISIT_MULTIPLIER;
    }

    return storage.local.get(AD_BLOCKER_MULTIPLIER_KEY) || VISIT_MULTIPLIER;
}

/**
 * Uses visitNumber and multiplier to
 * determine if the blocker should be
 * displayed to the user on this visit.
 */
function blockerShouldDisplay(visitNumber: string, multiplier: string) {
    const visits = parseInt(visitNumber, 10);
    const frequency = parseInt(multiplier, 10);

    if ((visits - 2) % frequency !== 0) {
        return false;
    }

    return true;
}

/**
 * Checks to see if user has localStorage
 * and sessionStorage
 */
function userHasStorage(storage: StorageArg) {
    const local = storage.local.get;
    const session = storage.session.get;

    return Boolean(typeof local === "function" && typeof session === "function");
}

/**
 * Checks to see if user has already been
 * blocked in this visit.
 */
function userShouldNotBeBlocked(storage: StorageArg) {
    return Boolean(storage.session.get(AD_BLOCKER_INCREMENTED_KEY));
}

/**
 * Runs a countdown timer in modal
 * when duration has been decreased
 * to zero, changes styles on link
 * and allows user to close modal.
 */
export function countdownAndReset(duration: number) {
    let timer = duration;
    const DELAY = 1000; // 1 second
    // eslint-disable-next-line prefer-const
    let interval: NodeJS.Timer;
    const container = document.getElementById(CONTAINER_ID) as HTMLElement;
    const continueButton = document.getElementById(CONTINUE_LINK_ID) as HTMLElement;
    const timerDiv = document.getElementById(TIMER_ID) as HTMLDivElement;

    function clickListener() {
        container.setAttribute("style", "display:none");
        stopListening();
    }

    function stopListening() {
        continueButton.removeEventListener("click", clickListener);
    }

    function count() {
        timerDiv.textContent = timer as unknown as string;
        if (--timer < 0) {
            clearInterval(interval);
            timerDiv.setAttribute("style", "display:none");
            continueButton.style.color = "#2800D7";
            continueButton.style.cursor = "pointer";
            continueButton.setAttribute("aria-label", "Continue to site");
            continueButton.addEventListener("click", clickListener);
        }
    }

    interval = setInterval(count, DELAY);
}

/**
 * Attempts to fetch from a google adsense URL
 * if the attempt succeeds, we can assume the
 * user is not using an ad blocker. If the attempt fails,
 * we assume the user is using an ad blocker.
 *
 * @returns {Promise}
 */
function checkForAdBlockerScript() {
    return fetch(
        "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js",
        {
            method: "HEAD",
            mode: "no-cors"
        }
    )
        .then(() => false)
        .catch(() => true);
}
